/**
 * The browser half of the font port — L1, L5.
 *
 * This code used to live in `packages/corpus/src/loaders.ts`. It moved out because
 * `FontFace` and `document.fonts` do not exist in React Native and the corpus package
 * is now imported by `apps/mobile` too. The LAW did not move: `loadPageWithFonts`
 * takes a `FontLoader` as a required argument and awaits it, so page data still cannot
 * reach a component ahead of the font that renders it.
 */
import { V4_FAMILY, type FontLoader } from '@mushaf/corpus'

const loadedFaces = new Set<string>()

/**
 * `font-display` must be BLOCK, not swap — there is no acceptable fallback to swap
 * from. A swap would paint a screen of tofu and then replace it.
 */
async function addFace(family: string, url: string, descriptors: FontFaceDescriptors = {}) {
  const key = `${family}|${url}`
  if (loadedFaces.has(key)) return
  const face = new FontFace(family, `url(${JSON.stringify(url)})`, {
    display: 'block',
    ...descriptors,
  })
  await face.load()
  document.fonts.add(face)
  loadedFaces.add(key)
}

/**
 * L5: ALL 604 v4 TAJWEED COLOUR FONTS REGISTER UNDER ONE FAMILY NAME.
 *
 * `@font-palette-values` is declared per family, and 604 families would need 604
 * duplicate palette declarations. Only one page is on screen at a time, so remove the
 * previous face before adding the next.
 */
let currentV4: FontFace | null = null

export const browserFonts: FontLoader = {
  async loadPageFaces(faces) {
    await Promise.all(
      faces.map(({ family, url }) =>
        addFace(family, url).catch(() => {
          // A missing page face is fatal to rendering, but throwing here would take the
          // whole page down; the measurement pass simply never converges instead.
          console.error(`failed to load page font ${family}`)
        }),
      ),
    )
  },

  async loadV4Face(_page, candidates) {
    if (currentV4) {
      document.fonts.delete(currentV4)
      currentV4 = null
    }

    // The candidate list is `.woff2` then `.ttf` — page 328 exists only as `.ttf`
    // upstream, so without the fallback one page of the mushaf is unrenderable (T6).
    for (const url of candidates) {
      try {
        const face = new FontFace(V4_FAMILY, `url(${JSON.stringify(url)})`, { display: 'block' })
        await face.load()
        document.fonts.add(face)
        currentV4 = face
        return true
      } catch {
        // Try the next candidate.
      }
    }
    // Loading the v4 font must be failure-tolerant: fall through to the monochrome
    // glyphs rather than blanking the page (§7.7).
    return false
  },

  v4FamilyFor: () => V4_FAMILY,
}
